import { useMemo, useState, useEffect, Suspense } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Spin } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  DollarOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  FundOutlined,
  ShoppingOutlined,
  SettingOutlined,
  UserOutlined,
  BankOutlined,
  AlertOutlined,
  LogoutOutlined,
  BarChartOutlined,
  FileSearchOutlined,
  GiftOutlined,
  FileMarkdownOutlined,
  FileProtectOutlined,
  MessageOutlined,
  BgColorsOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  AuditOutlined,
  LoginOutlined,
  AppstoreOutlined,
  RobotOutlined,
  ClusterOutlined,
  NotificationOutlined,
  CommentOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Result } from 'antd';
import { usePlatformAuth } from '../stores/auth.platform';
import { platformApi } from '../api/platform';
import { firstAllowedPath, hasPerm, requiredPermsForPath } from '../constants/permissions';

const { Sider, Content, Header } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

export default function PlatformLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = usePlatformAuth();
  const permissions = usePlatformAuth((s) => s.permissions);

  // RBAC：无 keyword_alerts.view 的角色不轮询角标（否则每 60s 白吃一个 403）
  const canViewAlerts = hasPerm(permissions, ['keyword_alerts.view']);
  const { data: alerts } = useQuery({
    queryKey: ['keyword-unresolved-count'],
    queryFn: () => platformApi.unresolvedKeywordAlertCount(),
    refetchInterval: 60_000,
    enabled: canViewAlerts,
  });
  const unread = alerts?.count ?? 0;

  const menu: MenuItem[] = useMemo(() => {
    const all: MenuItem[] = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '工作台' },
    {
      key: 'g-tenant',
      icon: <TeamOutlined />,
      label: '租户与用户',
      children: [
        { key: '/tenants', icon: <TeamOutlined />, label: '租户管理' },
        { key: '/group-relations', icon: <ClusterOutlined />, label: '集团关系' },
        { key: '/tenant-relations', icon: <BankOutlined />, label: '集团关系申请记录' },
        // 菜单名是「用户管理」而非「个人用户」：本页含全部真实用户
        // （个人散户 + 企业/集团/政府员工），且按「一身份一行」展开。
        { key: '/individual-users', icon: <UserOutlined />, label: '用户管理' },
      ],
    },
    {
      key: 'g-finance',
      icon: <DollarOutlined />,
      label: '财务与订单',
      children: [
        { key: '/finance', icon: <FundOutlined />, label: '财务看板' },
        { key: '/orders', icon: <DollarOutlined />, label: '订单管理' },
        { key: '/invoices', icon: <FileTextOutlined />, label: '发票审核' },
        { key: '/bank-transfer-reviews', icon: <FileDoneOutlined />, label: '对公转账审核' },
        { key: '/pending-charges', icon: <AlertOutlined />, label: '待回填扣费' },
        { key: '/plans', icon: <ShoppingOutlined />, label: '套餐管理' },
        { key: '/recharge-promotions', icon: <GiftOutlined />, label: '充值赠送' },
      ],
    },
    {
      key: 'g-geo-content',
      icon: <BarChartOutlined />,
      label: 'GEO管理',
      children: [
        { key: '/geo-content/entitlements', icon: <ShoppingOutlined />, label: '套餐与开通' },
        { key: '/geo-content/templates', icon: <FileTextOutlined />, label: '规则模板' },
      ],
    },
    {
      key: 'g-ai',
      icon: <RobotOutlined />,
      label: 'AI 与运营',
      children: [
        { key: '/ai-usage', icon: <BarChartOutlined />, label: 'AI 调用看板' },
        { key: '/rag', icon: <FileSearchOutlined />, label: 'RAG 知识库管理' },
        { key: '/messages-archive', icon: <FileSearchOutlined />, label: '流转审计' },
        { key: '/audit-config', icon: <AuditOutlined />, label: '审计配置' },
        {
          key: '/keyword-alerts',
          icon: <AlertOutlined />,
          label: <span>关键词告警 <Badge count={unread} size="small" offset={[4, -2]} /></span>,
        },
        { key: '/app-visits', icon: <LoginOutlined />, label: '来访记录' },
        { key: '/card-channels', icon: <LoginOutlined />, label: '名片渠道' },
      ],
    },
    {
      key: 'g-content',
      icon: <FileMarkdownOutlined />,
      label: '内容与反馈',
      children: [
        { key: '/cms', icon: <FileMarkdownOutlined />, label: 'FAQ / 帮助文档' },
        // 三类法律/介绍文档独立菜单 —— 同表（LegalDoc）按 doc_type 区分
        { key: '/legal-docs/privacy_policy', icon: <FileProtectOutlined />, label: '隐私协议' },
        { key: '/legal-docs/voiceprint_agreement', icon: <FileProtectOutlined />, label: '声纹授权协议' },
        { key: '/legal-docs/tos', icon: <FileProtectOutlined />, label: '用户协议' },
        { key: '/legal-docs/about_us', icon: <FileProtectOutlined />, label: '关于我们' },
        { key: '/feedbacks', icon: <MessageOutlined />, label: '反馈管理' },
      ],
    },
    {
      key: 'g-operations',
      icon: <NotificationOutlined />,
      label: '运营管理',
      children: [
        { key: '/operations/chat-suggestions', icon: <CommentOutlined />, label: '聊天推荐词' },
        { key: '/mall-products', icon: <ShoppingOutlined />, label: '商品管理' },
      ],
    },
    {
      key: 'g-system',
      icon: <AppstoreOutlined />,
      label: '系统设置',
      children: [
        // 主题配置用户要求放回（原暂隐藏）
        { key: '/themes', icon: <BgColorsOutlined />, label: '主题配置' },
        { key: '/card-templates', icon: <BgColorsOutlined />, label: '名片模版' }, // 双风格
        { key: '/system-config', icon: <SettingOutlined />, label: '系统配置' },
      ],
    },
    {
      key: 'g-security',
      icon: <SafetyCertificateOutlined />,
      label: '权限与审计',
      children: [
        { key: '/staff', icon: <KeyOutlined />, label: '运营账号' },
        { key: '/roles', icon: <SafetyCertificateOutlined />, label: '角色权限' },
        { key: '/audit-logs', icon: <AuditOutlined />, label: '操作审计' },
        { key: '/login-logs', icon: <LoginOutlined />, label: '登录日志' },
      ],
    },
    {
      key: 'g-service-hall',
      icon: <CustomerServiceOutlined />,
      label: '服务大厅',
      children: [
        { key: '/service-hall', icon: <CustomerServiceOutlined />, label: '服务大厅管理' },
      ],
    },
    ];
    // RBAC：按当前账号权限过滤菜单（无权限项隐藏，分组下全无则整组隐藏）
    const visible = (key: unknown) => hasPerm(permissions, requiredPermsForPath(String(key)));
    return all
      .map((item) => {
        const g = item as { key?: string; children?: MenuItem[] };
        if (!g?.children) return visible(g?.key) ? item : null;
        const children = g.children.filter((c) => visible((c as { key?: string })?.key));
        return children.length ? ({ ...(item as object), children } as MenuItem) : null;
      })
      .filter(Boolean) as MenuItem[];
  }, [unread, permissions]);

  // 路径 → 所属一级分组 key（按 child 路径前缀匹配；取最长前缀）
  const groupKeyForPath = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of menu) {
      const group = g as { key?: string; children?: { key?: string }[] };
      if (!group?.children) continue;
      for (const c of group.children) {
        if (c?.key) map.set(c.key, group.key as string);
      }
    }
    let best = '';
    let bestLen = 0;
    for (const [path, gk] of map) {
      if ((location.pathname === path || location.pathname.startsWith(path + '/')) && path.length > bestLen) {
        best = gk;
        bestLen = path.length;
      }
    }
    return best;
  }, [location.pathname, menu]);

  // 受控 openKeys：手风琴（同一时间只开一个分组）+ 跟随路径自动展开
  const [openKeys, setOpenKeys] = useState<string[]>(groupKeyForPath ? [groupKeyForPath] : []);
  useEffect(() => {
    if (groupKeyForPath) setOpenKeys([groupKeyForPath]);
  }, [groupKeyForPath]);

  const onOpenChange: MenuProps['onOpenChange'] = (keys) => {
    // 手风琴：只保留最新打开的那一个（与已开的不同的那个 key）
    const latest = keys.find((k) => !openKeys.includes(k));
    setOpenKeys(latest ? [latest] : []);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider>
        <div style={{ color: '#fff', padding: 20, fontWeight: 600 }}>元睿 AI 总部</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          items={menu}
          onClick={(e) => navigate(e.key)}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', textAlign: 'right', padding: '0 24px' }}>
          <Dropdown
            menu={{
              items: [
                { key: 'logout', icon: <LogoutOutlined />, label: '退出', onClick: () => { logout(); navigate('/login'); } },
              ],
            }}
          >
            <span style={{ cursor: 'pointer' }}>
              <Avatar style={{ marginRight: 8 }}>{user?.username?.[0]?.toUpperCase()}</Avatar>
              {user?.nickname || user?.username || '管理员'}
            </span>
          </Dropdown>
        </Header>
        <Content style={{ padding: 24 }}>
          {/* RBAC 路由闸：菜单已隐藏无权限项，这里兜「直接敲 URL」；按最长前缀匹配 PATH_PERMS */}
          {!hasPerm(permissions, requiredPermsForPath(location.pathname)) ? (
            firstAllowedPath(permissions) ? (
              <Result
                status="403"
                title="无权限访问"
                subTitle="当前账号的角色未包含此页面权限，如需开通请联系超级管理员。"
                extra={<Button type="primary" onClick={() => navigate(firstAllowedPath(permissions)!)}>返回有权限的页面</Button>}
              />
            ) : (
              <Result status="403" title="无任何页面权限" subTitle="当前角色未分配任何页面权限，请联系超级管理员配置。" />
            )
          ) : (
            /* 路由级懒加载：各页 React.lazy 动态导入，切页时 Suspense 兜底加载态 */
            <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spin size="large" /></div>}>
              <Outlet />
            </Suspense>
          )}
        </Content>
      </Layout>
    </Layout>
  );
}
